import { useFormik } from 'formik';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import * as ManifestUI from '@project44/manifest-ds';
import { DtoBase, TenantModels } from '@project44-models/iam-gateway';

import { TAnyOfPermissionsHOC } from '../../../../../shared/core/infrastructrue/types';
import { TUseSwitchTenantHook, TUseTenantQueryHook } from '../../../domain';
import { TenantSwitcherComponent } from '../TenantSwitcher/TenantSwitcher.component';

export type TTenantSwitcher = TAnyOfPermissionsHOC<{
  isFeatureFlagIam1057Enabled: boolean;
}> & {
  isInView?: boolean;
  toggleTenantSwitcherComponent?: () => void;
  UserPrincipalTenantTileCardComponent: React.ReactElement;
  useTenantQueryHook: TUseTenantQueryHook;
  useSwitchTenantHook: TUseSwitchTenantHook;
  tenantSwitcherComponent: TenantSwitcherComponent;
};

export const TenantSwitcherView: React.FC<TTenantSwitcher> = ({
  passThroughComponent,
  UserPrincipalTenantTileCardComponent,
  useSwitchTenantHook,
  isFeatureFlagIam1057Enabled,
  isInView,
  useTenantQueryHook,
  toggleTenantSwitcherComponent,
  tenantSwitcherComponent,
  user,
}) => {
  const { spaceSmall } = ManifestUI.designTokens;
  const { push } = useHistory();

  const { pathname } = useLocation();
  const inputElementRef = React.useRef<HTMLInputElement | null>(null);

  const [tenantQueryDto, setTenantQueryDto] = React.useState<
    DtoBase<TenantModels.TenantQueryViewModel> | undefined
  >(undefined);

  const tenantQueryDataState = useTenantQueryHook({
    tenantQueryDto,
  });

  const [switchTenantState, switchTenant] = useSwitchTenantHook();

  const { mutate, isLoading, ...rest } =
    tenantSwitcherComponent.handleTenantQueryResultsDataState({
      switchTenantAxiosDataState: switchTenantState,
      tenantQueryResultsDataState: tenantQueryDataState,
    });

  const formik = useFormik({
    initialValues: {
      searchQuery: '',
    },
    enableReinitialize: false,
    onSubmit: async (values) => {
      setTenantQueryDto(
        await tenantSwitcherComponent.buildTenantQueryDto(
          values.searchQuery,
          mutate,
        ),
      );
    },
  });

  React.useEffect(() => {
    tenantSwitcherComponent.handleSwitchTenantState({
      switchTenantState,
    });
  }, [switchTenantState]);

  const handleClearSearchOnClick = () => {
    formik.setFieldValue('searchQuery', '');
    formik.handleSubmit();
  };

  React.useEffect(() => {
    if (!!isInView) {
      inputElementRef.current?.focus();
    }
  }, [isInView]);

  return !isFeatureFlagIam1057Enabled ? (
    <>{passThroughComponent}</>
  ) : (
    <ManifestUI.Card
      css={{
        minWidth: '352px',
        maxWidth: '352px',
        padding: `${spaceSmall} ${spaceSmall} 0 ${spaceSmall}`,
      }}
    >
      <ManifestUI.Grid
        aria-label={'tenant-switcher-component'}
        columns={'repeat(12, 1fr)'}
        css={{ width: '100%' }}
        role={'presentation'}
      >
        <ManifestUI.GridItem
          column={12}
          as={'form'}
          css={{ position: 'relative' }}
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            formik.handleSubmit(event);
          }}
        >
          <ManifestUI.TextField
            role={'input'}
            aria-label={'Search by Name, ID, or UUID'}
            css={{ width: '100%' }}
            placeholder={'Search by Name, ID, or UUID'}
            size={'small'}
            id={'searchInputField'}
            startIcon={<ManifestUI.Icon icon={'search'} />}
            isDisabled={isLoading || switchTenantState.isLoading}
            inputRef={inputElementRef}
            value={formik.values.searchQuery}
            onChange={(currentValue) => {
              formik.setFieldValue('searchQuery', currentValue);
            }}
          />
          <ManifestUI.Box
            aria-label={'Clear Search'}
            role={'button'}
            css={{
              display: 'flex',
              fontSize: '6px',
              height: '32px',
              justifyContent: 'center',
              position: 'absolute',
              right: '1px',
              top: '1px',
              width: '32px',
              ...(!isLoading
                ? {
                    '&:hover': {
                      background:
                        ManifestUI.designTokens.colorBackgroundSecondary,
                      cursor: 'pointer',
                    },
                  }
                : {
                    pointerEvents: 'none',
                  }),
            }}
            onClick={handleClearSearchOnClick}
          >
            <ManifestUI.Icon css={{ fontSize: '12px' }} icon={'close'} />
          </ManifestUI.Box>

          {tenantSwitcherComponent
            .handleComposeRenderedElements(
              {
                user,
                tenantQueryResultsDataState: {
                  ...rest,
                  isLoading,
                  mutate,
                },
                searchQuery: formik.values.searchQuery,
                switchTenant,
              },
              UserPrincipalTenantTileCardComponent,
            )
            ?.renderer({})}
        </ManifestUI.GridItem>
        <ManifestUI.GridItem
          column={12}
          css={{
            background: ManifestUI.designTokens.colorBackgroundSecondary,
            display: 'flex',
            justifyContent: 'center',
            marginLeft: `calc(0px - ${spaceSmall})`,
            width: `calc(100% + ${spaceSmall} + ${spaceSmall})`,
          }}
        >
          {tenantSwitcherComponent.handleRenderTenantManagementUILink({
            tenantQueryResultsDataState: {
              ...rest,
              isLoading,
              mutate,
            },
            locationPathName: pathname,
            pushHistoryFn: push,
            searchQuery: formik.values.searchQuery,
            toggleTenantSwitcherComponent,
          })}
        </ManifestUI.GridItem>
        <ManifestUI.GridItem column={12}>
          <ManifestUI.Separator
            css={{
              marginLeft: `calc(0px - ${spaceSmall})`,
              width: `calc(100% + ${spaceSmall} + ${spaceSmall})`,
            }}
          />
        </ManifestUI.GridItem>
        <ManifestUI.GridItem
          column={12}
          css={{
            margin: `${spaceSmall} 0`,
          }}
        >
          {passThroughComponent}
        </ManifestUI.GridItem>
      </ManifestUI.Grid>
    </ManifestUI.Card>
  );
};
